import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { FeedComment } from '../../types';
import './CommentsModal.css';

interface CommentsModalProps {
  targetType: 'activity' | 'feed_post';
  targetId: number;
  currentUserId: number;
  onClose: () => void;
}

const CommentsModal: React.FC<CommentsModalProps> = ({ targetType, targetId, currentUserId, onClose }) => {
  const { t } = useLanguage();
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [targetType, targetId]);

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/feed/comments/${targetType}/${targetId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/feed/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_type: targetType, target_id: targetId, text: trimmed }),
      });
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [...prev, newComment]);
        setText('');
      }
    } catch {
      // silent
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/feed/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
      }
    } catch {
      // silent
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="comments-overlay" onClick={onClose}>
      <div className="comments-modal" onClick={e => e.stopPropagation()}>
        <div className="comments-header">
          <h3>{t('feed.comments')}</h3>
          <button className="comments-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="comments-list">
          {loading ? (
            <div className="comments-empty">{t('common.loading')}</div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">{t('feed.noComments')}</div>
          ) : (
            comments.map(c => (
              <div key={c.id} className="comment-item">
                {c.user_profile_picture ? (
                  <img src={c.user_profile_picture} alt="" className="comment-avatar" />
                ) : (
                  <div className="comment-avatar-placeholder">
                    {c.is_ai_generated ? '🤖' : (c.user_first_name?.[0] || '?')}
                  </div>
                )}
                <div className="comment-body">
                  <div className="comment-author">
                    {c.user_first_name} {c.user_last_name}
                    {c.is_ai_generated && <span className="ai-badge">🤖 {t('feed.aiCoach')}</span>}
                  </div>
                  <div className="comment-text">{c.text}</div>
                  <div className="comment-meta">
                    <span className="comment-time">{formatTime(c.created_at)}</span>
                    {c.user_id === currentUserId && !c.is_ai_generated && (
                      <button className="comment-delete-btn" onClick={() => handleDelete(c.id)}>
                        {t('feed.deleteComment')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="comments-compose">
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('feed.addComment')}
            maxLength={500}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
          />
          <button onClick={handleSend} disabled={!text.trim() || sending}>
            {t('feed.send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentsModal;
